import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useOrganization from '../../services/organizations/useOrganization'
import useActivateSubscription from '../../services/subscription/useActivateSubscription'
import PageTitle from '../../components/UI/PageTitle'
import Spinner from '../../components/UI/Spinner'
import OrganizationStatus from '../../components/UI/OrganizationStatus'
import BackBtn from '../../components/Buttons/BackBtn'
import Button from '../../components/Buttons/Button'
import Divider from '../../components/UI/Divider'
import UpdateEodModal from '../../components/Modals/UpdateEodModal'
import OrganizationDetailsUsers from '../../components/OrganizationDetailsUsers/OrganizationDetailsUsers'
import { transformDate } from '../../helpers/transformDate'
import { formatPaymentMethod } from '../../helpers/formatPaymentMethod'
import { isProvisionalPeriod } from '../../helpers/isProvisionalPeriod'

const Organization = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data: organization, isLoading } = useOrganization(id!)
  const { mutate: activateSubscription, isLoading: isActivating } =
    useActivateSubscription(id!)
  const [isEodModalOpen, setIsEodModalOpen] = useState(false)

  useEffect(() => {
    document.title = `${t('organization')} - ReportAll`
  }, [t])

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-full'>
        <Spinner spinner='table' width='w-11' height='h-11' color='#0C75FF' />
      </div>
    )
  }

  const isActive = organization?.status === 'active'
  // Activation is addressed by subscription, not organisation. The detail
  // serializer exposes the id, but it is null for an organisation with no
  // subscription at all - and there is nothing this page can safely guess.
  const subscriptionId = organization?.subscription_id
  // The button means "the transfer arrived", so it belongs only on an
  // organisation that chose bank transfer and has not paid. `payment_method`
  // is the only bank-transfer marker this endpoint exposes - the
  // subscription's `provider` is not serialised - and `bank_transfer` is
  // written solely by `BankTransferService.create_signup`; Stripe's webhook
  // writes `card`, `sepa_debit` or `other`.
  const isBankTransfer = organization?.payment_method?.type === 'bank_transfer'
  // The unpaid statuses `BankTransferService.activate` accepts: `pending`
  // (signed up, never paid) and `past_due`/`unpaid` (a renewal went unpaid).
  // It also accepts `active`, to extend an early renewal, which is a
  // different job from confirming a first payment.
  const isAwaitingPayment =
    organization?.status === 'pending' ||
    organization?.status === 'past_due' ||
    organization?.status === 'unpaid'
  const canActivate = isBankTransfer && isAwaitingPayment
  const subscriptionStartDate = transformDate(
    organization?.subscription_start_date,
    'DD/MM/YY',
  )
  const subscriptionEndDate = transformDate(
    organization?.subscription_end_date,
    'DD/MM/YY',
  )
  const subscriptionPeriod =
    subscriptionStartDate || subscriptionEndDate
      ? `${subscriptionStartDate || '-'} - ${subscriptionEndDate || '-'}`
      : '-'
  // Dates are present but provisional until the transfer is confirmed, so say
  // so rather than let them read as a settled period.
  const isPeriodProvisional =
    isProvisionalPeriod(organization?.status) && subscriptionPeriod !== '-'

  return (
    <>
      <div className='flex items-center justify-between gap-5 flex-wrap'>
        <div className='flex items-center gap-3 sm:gap-4 flex-wrap'>
          <BackBtn variant='icon' />
          <PageTitle title={t('organization')} />
          <span className='text-[28px] font-bold'>
            {organization?.id ?? ''}
          </span>
          <OrganizationStatus
            status={organization?.status ?? null}
            className='mx-0'
          />
        </div>

        <div className='flex items-center gap-2.5 flex-wrap'>
          <Button
            type='button'
            text={t('viewTransactions')}
            width='w-max'
            bgColor='bg-[#E1ECFC]'
            textColor='text-black'
            onClickHandler={() =>
              navigate(`/super-admin/transactions?organization=${id}`)
            }
          />
          {canActivate && (
            <Button
              type='button'
              text={t('activateAccount')}
              width='w-max'
              disabled={isActivating || !subscriptionId}
              title={subscriptionId ? undefined : t('noSubscriptionToActivate')}
              onClickHandler={() =>
                subscriptionId && activateSubscription(subscriptionId)
              }
            />
          )}
        </div>
      </div>
      <div className='w-full p-5 sm:p-12 bg-white rounded-20px shadow-form mt-7'>
        <h2 className='font-bold text-[24px]'>{t('companyProfile')}</h2>
        <div className='mt-6 grid gap-5 sm:grid-cols-3 sm:gap-0'>
          <InfoDetails
            title={t('companyName')}
            value={organization?.company_name}
          />
          <InfoDetails title={t('email')} value={organization?.company_email} />
        </div>
        <Divider />
        <h2 className='font-bold text-[24px]'>{t('subscription')}</h2>
        <div className='mt-6 grid gap-5 sm:grid-cols-3 sm:gap-0'>
          <InfoDetails
            title={t('paymentMethod')}
            value={formatPaymentMethod(t, organization?.payment_method?.type)}
          />
          <div className='space-y-4 sm:w-max overflow-x-hidden'>
            <h3 className='text-lg font-semibold'>{t('subscriptionPeriod')}</h3>
            <div className='flex items-center gap-4 flex-wrap'>
              <p className='text-lg text-lightGray'>
                {subscriptionPeriod}
                {isPeriodProvisional && (
                  <span className='ml-2 text-sm italic'>
                    ({t('provisionalPeriod')})
                  </span>
                )}
              </p>
              {isActive && (
                <button
                  type='button'
                  onClick={() => setIsEodModalOpen(true)}
                  className='text-lg font-bold text-primaryBlue hover:underline'
                >
                  {t('updateEod')}
                </button>
              )}
            </div>
          </div>
        </div>
        <Divider />

        <OrganizationDetailsUsers users={organization?.users ?? []} />
      </div>

      {isEodModalOpen && (
        <UpdateEodModal
          organizationId={id!}
          currentEndDate={organization?.subscription_end_date}
          onClose={() => setIsEodModalOpen(false)}
        />
      )}
    </>
  )
}

export default Organization

function InfoDetails({
  title,
  value,
}: {
  title: string
  value?: string | null
}) {
  const { t } = useTranslation()
  return (
    <div className='space-y-4 sm:w-max overflow-x-hidden'>
      <h3 className='text-lg font-semibold'>{t(title)}</h3>

      <p className='text-lg text-lightGray line-clamp-1 text-ellipsis'>
        {value ?? '-'}
      </p>
    </div>
  )
}
